import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Masonry from 'react-responsive-masonry';
import { ProfileModel } from '../../../../core/models/ProfileModel';
import { observer } from 'mobx-react';
import { EmptyWidget } from '../../../../widgets/content/EmptyWidget';
import { keyframes } from '@emotion/react';
import { LightBoxContext } from '../../../../widgets/layers/LightBoxWidget';
import { useResponsive } from '../../../../hooks/useResponsive';
import { ButtonWidget } from '../../../../widgets/controls/ButtonWidget';
import { useNavigate } from 'react-router-dom';
import { Routing } from '../../../Routing';
import { LoadingWidget } from '../../../../widgets/content/LoadingWidget';

export interface ProfileGalleryWidgetProps {
  profile: ProfileModel;
  className?: any;
}

export const ProfileGalleryWidget: React.FC<ProfileGalleryWidgetProps> = observer((props) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    props.profile.loadGalleryImages().then(() => {
      setLoading(false);
    });
  }, [props.profile]);

  return (
    <S.Container className={props.className}>
      <ProfileGalleryControls profile={props.profile} />
      <LoadingWidget loading={loading}>
        {() => {
          return <ProfileGalleryContent profile={props.profile} />;
        }}
      </LoadingWidget>
    </S.Container>
  );
});

export interface ProfileGalleryControlsProps {
  profile: ProfileModel;
}

export const ProfileGalleryControls: React.FC<ProfileGalleryControlsProps> = observer((props) => {
  const navigate = useNavigate();
  if (!props.profile.isMe) {
    return null;
  }

  return (
    <S.Buttons>
      <ButtonWidget
        label="Upload"
        action={() => {
          navigate(Routing.UPLOAD_GALLERY_IMAGE, { state: 'push' });
        }}
      />
    </S.Buttons>
  );
});

export interface ProfileGalleryContentProps {
  profile: ProfileModel;
}

export const ProfileGalleryContent: React.FC<ProfileGalleryContentProps> = observer((props) => {
  const imageContext = useContext(LightBoxContext);
  const { desktop } = useResponsive();

  if (props.profile.gallery_images.length === 0) {
    return <EmptyWidget label="No images yet!" />;
  }

  return (
    <Masonry columnsCount={desktop ? 6 : 3} gutter="10px">
      {props.profile.gallery_images.map((i, index) => {
        return (
          <S.Image
            key={i._id.toString()}
            onClick={() => {
              imageContext.setImage(i);
            }}
            delay={index * 50}
            src={i.url}
          />
        );
      })}
    </Masonry>
  );
});
namespace S {
  export const FadeIn = keyframes`
    0%{
      opacity: 0;
    }
    100%{
      opacity: 1;
    }
  `;

  export const Buttons = styled.div`
    display: flex;
    align-content: center;
    justify-content: flex-start;
    gap: 5px;
  `;

  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
  `;

  export const Image = styled.img<{ delay: number }>`
    width: 100%;
    display: block;
    border-radius: 4px;
    animation-delay: ${(p) => p.delay}ms;
    animation-name: ${FadeIn};
    animation-duration: 0.4s;
    animation-fill-mode: forwards;
    animation-timing-function: ease-out;
    opacity: 0;
    cursor: pointer;
    border: solid 2px transparent;
    transition: border-color 0.3s;

    &:hover {
      border: solid 2px ${(p) => p.theme.button_primary.background};
    }
  `;
}
